import React, { useEffect } from 'react';
import { Actions, Alert, Autosuggest, Box, Button, Divider, filterSuggestions, IconDate, IconLocation, IconProfile, IconTime, Stack, Text, TextField, Tiles } from 'braid-design-system';
import { useFinancialDate } from '../contexts/FinancialDateProvider';
import { useAuth } from '../contexts/AuthProvider';
import { cashierSignIn } from '../services/cashierService';
import { useCashier } from '../contexts/CashierProvider';
import { getCashBooks } from '../services/cashBookService';
import { useToast } from '../hooks/useToast';
import { useLogger } from '../hooks/useLogger';
import Loading from '../components/Loading';
import { useLocalDateTime } from '../contexts/LocalDateTimeProvider';
import { Schemas } from '../types/api-types';
import { schemas } from '../types/zod-api-schema';
import { useTranslation } from 'react-i18next';

type CashBookSuggestion = { text: string; value: number };
type CashBook = Schemas['CashBookResponse']

type CashierSignOnProps = {
    setCashierDrawerOpen?: (open: boolean) => void;
};

const CashierSignOn: React.FC<CashierSignOnProps> = ({ setCashierDrawerOpen }) => {
    const [cashBooks, setCashBooks] = React.useState<CashBook[]>([]);
    const cashBookSuggestions: { text: string; value: CashBookSuggestion }[] = cashBooks.map(book => ({
        text: book.cash_book_name,
        value: { text: book.cash_book_name, value: book.cash_book_id }
    }));

    const [cashBookInput, setCashBookInput] = React.useState('');
    const [selectedCashBook, setSelectedCashBook] = React.useState<CashBookSuggestion | null>(null);
    const [openingCash, setOpeningCash] = React.useState<string>('');
    const [error, setError] = React.useState('');
    const [fieldErrors, setFieldErrors] = React.useState<{ cash_book?: string; amount?: string }>({});
    const [loading, setLoading] = React.useState(false);
    const { currentFinancialDate } = useFinancialDate();
    const { user } = useAuth();
    const { setCashBook, setIsSignedIn } = useCashier();
    const toast = useToast();
    const logger = useLogger();
    const { dateTime } = useLocalDateTime();
    const { t } = useTranslation();

    useEffect(() => {
        getCashBooks()
            .then((cashBooks) => setCashBooks(cashBooks))
            .catch(() => {
                toast.showError(
                    t('page.cashier_sign_on.error.load_cash_books', {
                        defaultValue: 'Failed to load cash books',
                    }),
                );
            });
    }, [toast, t]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setFieldErrors({});
        setError('');

        const request = {
            cash_book: selectedCashBook ? Number(selectedCashBook.value) : null,
            amount: openingCash ? parseFloat(openingCash) : null,
        }

        const result = schemas.CashierSignInRequest.safeParse(request);

        if (!result.success) {
            // Map Zod issues to field errors
            const errors: { cash_book?: string; amount?: string } = {};
            result.error.issues.forEach((issue: any) => {
                if (issue.path[0] === 'cash_book') errors.cash_book = issue.message;
                if (issue.path[0] === 'amount') errors.amount = issue.message;
            });
            setFieldErrors(errors);
            return;
        }

        setLoading(true);
        try {
            await cashierSignIn(result.data);
            setIsSignedIn(true);
            setCashBook(cashBooks.find(book => book.cash_book_id === result.data.cash_book)!);
            if (setCashierDrawerOpen) {
                setCashierDrawerOpen(false);
            }
            toast.showSuccess(
                t('page.cashier_sign_on.toast.success', {
                    defaultValue: 'Cashier signed in successfully',
                }),
            );
        } catch (err: any) {
            const errorMessage =
                typeof err.message === 'string'
                    ? err.message
                    : t('page.cashier_sign_on.error.sign_in_failed', {
                        defaultValue: 'Sign in failed, please try again',
                    });
            setError(errorMessage);
            logger.error('Cashier sign in failed', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} style={{ width: '100%' }} autoComplete="off">
            <Stack space="small">
                {loading && (
                    <Loading
                        message={t('page.cashier_sign_on.loading.signing_in', {
                            defaultValue: 'Signing you in. Please wait…',
                        })}
                    />
                )}
                <Stack space="large">
                    {!!error &&
                        <Box width="full">
                            <Alert tone="critical"><Text>{error}</Text></Alert>
                        </Box>
                    }
                    <Stack space="small">
                        <Text icon={<IconProfile />}>
                            {user
                                ? user.name
                                : t('page.cashier_sign_on.placeholder.cashier_name', {
                                    defaultValue: 'Cashier Name Here',
                                })}
                        </Text>
                        <Text icon={<IconLocation />} tone="secondary">
                            {user
                                ? user.active_branch?.name_ln1
                                : t('page.cashier_sign_on.placeholder.branch_name', {
                                    defaultValue: 'Branch Name Here',
                                })}
                        </Text>
                        <Text icon={<IconDate />} tone="secondary">
                            {currentFinancialDate
                                ? new Date(currentFinancialDate.business_date).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                })
                                : t('page.cashier_sign_on.placeholder.date', {
                                    defaultValue: 'Loading date...',
                                })}
                        </Text>
                        <Text icon={<IconTime />} tone="secondary">
                            {dateTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
                        </Text>
                    </Stack>
                    <Divider />
                    <Stack space="small">
                        <Tiles columns={[1, 2]} space="small">
                            <Autosuggest<CashBookSuggestion>
                                label={t('page.cashier_sign_on.form.cash_book.label', {
                                    defaultValue: 'Cash Book',
                                })}
                                placeholder={t(
                                    'page.cashier_sign_on.form.cash_book.placeholder',
                                    { defaultValue: 'Select Cash Book Account' },
                                )}
                                value={{
                                    text: cashBookInput,
                                    value: selectedCashBook ?? undefined
                                }}
                                onChange={value => {
                                    setCashBookInput(value.text);
                                    setSelectedCashBook(value.value ?? null);
                                }}
                                onBlur={() => {
                                    if (!selectedCashBook) {
                                        setCashBookInput('');
                                    }
                                }}
                                onClear={() => {
                                    setCashBookInput('');
                                    setSelectedCashBook(null);
                                }}
                                suggestions={filterSuggestions(cashBookSuggestions)}
                                message={fieldErrors.cash_book}
                                tone={fieldErrors.cash_book ? 'critical' : undefined}
                                disabled={loading}
                            />
                            <TextField
                                label={t('page.cashier_sign_on.form.opening_cash.label', {
                                    defaultValue: 'Opening Cash Book',
                                })}
                                onChange={(event) => {
                                    const value = (event.target as HTMLInputElement).value;
                                    setOpeningCash(value);
                                }}
                                value={openingCash}
                                onClear={() => setOpeningCash('')}
                                                                message={fieldErrors.amount}
                                tone={fieldErrors.amount ? 'critical' : undefined}
                                disabled={loading}
                                placeholder={t(
                                    'page.cashier_sign_on.form.opening_cash.placeholder',
                                    { defaultValue: 'Enter Opening Cash Amount' },
                                )}
                            />
                        </Tiles>
                        <Box display="flex" justifyContent="flexEnd">
                            <Actions>
                                {loading ? (
                                    <Button variant="transparent" loading>
                                        {t('page.cashier_sign_on.buttons.sign_in', {
                                            defaultValue: 'Sign In',
                                        })}
                                    </Button>
                                ) : (
                                    <Button type="submit">
                                        {t('page.cashier_sign_on.buttons.sign_in', {
                                            defaultValue: 'Sign In',
                                        })}
                                    </Button>
                                )}
                            </Actions>
                        </Box>

                    </Stack>
                </Stack>
            </Stack>
        </form>
    );
};

export default CashierSignOn;
